import type { RefObject } from 'react';
import { useLayoutEffect } from 'react';

/**
 * Jumps the window and an optional target shell/element back to the top whenever `key` changes —
 * e.g. a wizard's step index or an exam's question index, so each new step or question opens
 * at its heading instead of where the previous one was scrolled.
 * Runs before paint, and `instant` overrides the global `html { scroll-behavior: smooth }`.
 */
export function useScrollTopOnChange<T extends HTMLElement | null = HTMLElement | null>(
  key: unknown,
  targetRef?: RefObject<T>,
): void {
  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });

    const el = targetRef?.current;
    if (!el) return;

    if (typeof el.scrollTo === 'function') {
      el.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    } else {
      el.scrollTop = 0;
      el.scrollLeft = 0;
    }

    // When wrapped inside a scrolling container (e.g. desktop /app shell or mobile shell),
    // also reset the container's scroll so the content starts at the top.
    const scrollableAncestor = el.closest?.('main') ?? el.parentElement;
    if (scrollableAncestor) {
      if (typeof scrollableAncestor.scrollTo === 'function') {
        scrollableAncestor.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      } else {
        scrollableAncestor.scrollTop = 0;
      }
    }

    if (typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'start', behavior: 'instant' });
    }
  }, [key, targetRef]);
}

